import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { type ReportSliceRow } from '@/domain/results/types';
import { formatCount, formatCtr } from '@/domain/results/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  title: string;
  column: string;
  rows: ReportSliceRow[];
}

/** "Por lugar no app" and "Por variante": a slice under 20 people shows no number. */
const ReportSlicesTable = ({ title, column, rows }: Props) => {
  const t = useTranslate('domain.results.components.ReportSlicesTable');

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2.5, pt: 2.5 }}>
        {title}
      </Typography>
      {rows.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{column}</TableCell>
              <TableCell align="right">{t('impressions')}</TableCell>
              <TableCell align="right">{t('clicks')}</TableCell>
              <TableCell align="right">{t('ctr')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>{row.label}</TableCell>
                {row.hidden ? (
                  <TableCell colSpan={3} align="right">
                    <Typography variant="caption" color="text.secondary">
                      {t('hidden')}
                    </Typography>
                  </TableCell>
                ) : (
                  <>
                    <TableCell align="right">{formatCount(row.counts.impressions)}</TableCell>
                    <TableCell align="right">{formatCount(row.counts.clicks)}</TableCell>
                    <TableCell align="right">{formatCtr(row.counts.ctr)}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
          {t('empty')}
        </Typography>
      )}
    </Paper>
  );
};

export default ReportSlicesTable;

const sx = {
  paper: { borderRadius: radiusTokens.xxl, overflow: 'hidden' },
};
